/**
 * ท่าล็อกเป้า (lock moves): the moves whose use is a paired animation on both fighters, kept in this one table. A lock move
 * pressed out of reach walks to its target; once it takes effect (systems/duelLock.ts) the caster performs the move while
 * the target plays its receive sequence, and neither can act until it ends. About one per art: its signature combo.
 *
 * A form is told in beats over its length (0 = it connects, 1 = both are free again). The caster's keys are the move's
 * own swing (plus a short lunge toward the target); the target's receive keys are `receive`: `hit` (rocked by each blow),
 * `spin` (whirled round), `knock` (thrown back `dist` metres, a real displacement), `kneel` (down on a knee), `fall` (on its
 * back) and `recover` (getting up, easing back to stance). The client draws them on any figure, both sexes, from the one
 * hurt pose (render/passes/lockMarker.ts); the metres are the rules'. Data only: no DOM, no timers.
 */
export type FormBeatKind = 'hit' | 'spin' | 'knock' | 'kneel' | 'fall' | 'recover';

export interface FormBeat {
  readonly from: number;
  readonly to: number;
  readonly kind: FormBeatKind;
  /** `knock`: metres thrown back over the beat. */
  readonly dist?: number;
}

export interface LockFormDef {
  /** The caster steps this far toward the target (metres, never closer than 1 m) over the first fifth of the form. */
  readonly lunge: number;
  readonly receive: readonly FormBeat[];
}

/** A flurry: rocked by blow after blow, whirled, thrown back, and up again. */
const FLURRY: readonly FormBeat[] = [
  { from: 0, to: 0.45, kind: 'hit' },
  { from: 0.45, to: 0.7, kind: 'spin' },
  { from: 0.7, to: 0.86, kind: 'knock', dist: 1.3 },
  { from: 0.86, to: 1, kind: 'recover' },
];
/** A thrust: struck, driven back, and down on a knee. */
const THRUST: readonly FormBeat[] = [
  { from: 0, to: 0.25, kind: 'hit' },
  { from: 0.25, to: 0.55, kind: 'knock', dist: 1.6 },
  { from: 0.55, to: 0.82, kind: 'kneel' },
  { from: 0.82, to: 1, kind: 'recover' },
];
/** A palm: struck, flung away, and down on the back. */
const PALM: readonly FormBeat[] = [
  { from: 0, to: 0.2, kind: 'hit' },
  { from: 0.2, to: 0.5, kind: 'knock', dist: 2.2 },
  { from: 0.5, to: 0.78, kind: 'fall' },
  { from: 0.78, to: 1, kind: 'recover' },
];
/** A sweeping cut: struck, whirled round, down on a knee. */
const SLASH: readonly FormBeat[] = [
  { from: 0, to: 0.3, kind: 'hit' },
  { from: 0.3, to: 0.62, kind: 'spin' },
  { from: 0.62, to: 0.84, kind: 'kneel' },
  { from: 0.84, to: 1, kind: 'recover' },
];

/**
 * The lock moves, one or so an art (its signature). Every move here is `aimOf == 'lock'` (an attack that is not a long shot,
 * a wheel, a dash or ground-aimed).
 */
export const LOCK_FORMS: Readonly<Record<string, LockFormDef>> = {
  // กระบี่ไผ่ห่มหมอก (the starting art): 蘆 the three reeds
  'bs-reed': { lunge: 1.2, receive: FLURRY },
  // ดาบชาดคลั่งไฟ: 裂 the rend
  'bs-rend': { lunge: 1.0, receive: SLASH },
  // ฝ่ามือเมฆาคล้อย: 浪 the wave of the palm
  'cp-wave': { lunge: 0.8, receive: PALM },
  // หมัดหมอกวน: 推 the push
  'tf-push': { lunge: 0.6, receive: PALM },
  // กระบี่วงธารา: 流 the flow
  'ts-flow': { lunge: 1.0, receive: SLASH },
  // หมัดระฆังก้อง: 擊 the strike
  'ar-strike': { lunge: 0.8, receive: THRUST },
  // ดาบขอเกี่ยวโคม: 十 the cross
  'jy-cross': { lunge: 1.2, receive: FLURRY },
  // ฝ่ามือเงียบจันทร์มัว: 吸 the drain
  'jl-drain': { lunge: 0.6, receive: PALM },
  // กระบี่แท่งหมึกสน: 詩 the poem (three strokes)
  'jz-poem': { lunge: 1.0, receive: FLURRY },
  // ฝ่ามือก้องระฆัง: 千 the thousand palms
  'pp-thousand': { lunge: 0.8, receive: FLURRY },
  // ดาบค้อนช่างใหญ่: 十 the cross
  'fm-cross': { lunge: 1.0, receive: THRUST },
  // ไม้พลองคลื่นไผ่ (prototype, hidden): 壓 the pin, struck, down, the staff end held on the chest
  'tt-pin': {
    lunge: 0.8,
    receive: [
      { from: 0, to: 0.25, kind: 'hit' },
      { from: 0.25, to: 0.8, kind: 'fall' },
      { from: 0.8, to: 1, kind: 'recover' },
    ],
  },
  // กระบี่ธารเยือกแข็ง: 彈 the bough (long routine data/longMoves/snowSword.ts; was the ss-trace dash placeholder)
  'ss-shift': { lunge: 1.0, receive: SLASH },
  // หัตถ์ล็อกโซ่ทาง: 扣 the three-link shackle (long routine data/longMoves/arrestHand.ts; leaves a shackle)
  'ah-grip': { lunge: 0.8, receive: PALM },
  // เพลงหมู่เงาจันทร์: 袖 the silk top (long routine data/longMoves/demonDance.ts; works at sleeve length 2.0–2.4 m)
  'dd-sleeve': { lunge: 0.4, receive: PALM },
  // กระบี่จันทร์เสน่หา: 吻 the crescent dance (long routine data/longMoves/moonSword.ts; was the ms-moon placeholder)
  'ms-kiss': { lunge: 0.8, receive: SLASH },
  // ดาบชักเร็วโคมเหล็ก: 判 three verdicts (long routine data/longMoves/springSabre.ts; was the sp-flash placeholder)
  'sp-verdict': { lunge: 1.2, receive: THRUST },
  // ดาบผลักผาชาด: 鋒 the five-step march (long routine data/longMoves/redCliffSabre.ts; drives the foe 3 m to the ring line)
  'rc-edge': { lunge: 1.0, receive: SLASH },
  // ขีดเดียวไร้ถ้อย: 一 one stroke past the mountain (long routine data/longMoves/sageSword.ts; a pass, the wound opens late)
  'sg-line': { lunge: 0.6, receive: THRUST },
  // คีมเล็บพยัคฆ์ไฟ: 撕 the tiger pins its prey (long routine data/longMoves/bloodClaw.ts; was the bc-rake placeholder)
  'bc-maul': { lunge: 1.4, receive: PALM },
  // ── rules sweep placeholders (TODO-polish: own form per art; DUEL_QUEUE.md section P) ──
  'dp-regret': { lunge: 0.6, receive: PALM },
  'dp9-burst': { lunge: 0.6, receive: PALM },
  'ns-rush': { lunge: 1.0, receive: SLASH },
  // กระบี่ปลายแท่งหมึก: 定 the six strings (long routine data/longMoves/sixPulse.ts; ranged by design, aim forced)
  'sf-hold': { lunge: 0.6, receive: PALM },
  'hw-dart': { lunge: 0.8, receive: THRUST },
};

/** Moves whose own range/volley would derive a non-lock aim; the sweep forces `aim: 'lock'` (data/skills.ts). */
export const LOCK_AIM_FORCED: ReadonlySet<string> = new Set(['ns-rush', 'sf-hold', 'hw-dart']);

/** Wind-up seconds written into the data of every player lock move and power move that has none (data/skills.ts). */
export const WINDUP_DATA = {
  /** A lock move of one blow, and a flurry (2+ blows). */
  lockSingle: 0.35,
  lockFlurry: 0.45,
  /** A power move (unblockable) on one target, and an area one (wheel, ground). */
  powerSingle: 0.5,
  powerArea: 0.6,
} as const;
